import { jsx, jsxs } from "react/jsx-runtime";
import classNames from "classnames";
import debounce from "lodash.debounce";
import { useMemo, useEffect, useRef, useState } from "react";
import Button from "./Button.mjs";
/* empty css                */
const KEY_ALL = "all";
const buildMaps = (parentMap, nodeMap, nodes, parent = null) => {
  var _a;
  for (const node of nodes) {
    nodeMap.set(node.key, node);
    if (parent)
      parentMap.set(node.key, parent);
    if ((_a = node.children) == null ? void 0 : _a.length) {
      buildMaps(parentMap, nodeMap, node.children, node);
    }
  }
  return [parentMap, nodeMap];
};
const getAllDescendants = (nodeMap, id) => {
  var _a;
  const node = nodeMap.get(id);
  const result = [];
  if ((_a = node == null ? void 0 : node.children) == null ? void 0 : _a.length) {
    for (const child of node.children) {
      result.push(child.key, ...getAllDescendants(nodeMap, child.key));
    }
  }
  return result;
};
const getAllItems = (nodeMap, tree) => {
  let ids = [];
  for (const node of tree) {
    ids.push(node.key);
    const allDescendants = getAllDescendants(nodeMap, node.key);
    ids = ids.concat(allDescendants);
  }
  return ids;
};
const toggleSelectedId = (selectedItems, parentMap, nodeMap, targetId) => {
  const newSelected = new Set(selectedItems);
  const areAllChildrenSelected = (node) => {
    var _a;
    if (!((_a = node.children) == null ? void 0 : _a.length))
      return true;
    return node.children.every(
      (child) => newSelected.has(child.key) && areAllChildrenSelected(child)
    );
  };
  function checkAndSelectParents(id) {
    const parent = parentMap.get(id);
    if (parent && areAllChildrenSelected(parent)) {
      newSelected.add(parent.key);
      checkAndSelectParents(parent.key);
    }
  }
  function unselectParents(id) {
    const parent = parentMap.get(id);
    if (parent && newSelected.has(parent.key)) {
      newSelected.delete(parent.key);
      unselectParents(parent.key);
    }
  }
  if (newSelected.has(targetId)) {
    newSelected.delete(targetId);
    for (const descId of getAllDescendants(nodeMap, targetId)) {
      newSelected.delete(descId);
    }
    unselectParents(targetId);
  } else {
    newSelected.add(targetId);
    for (const descId of getAllDescendants(nodeMap, targetId)) {
      newSelected.add(descId);
    }
    checkAndSelectParents(targetId);
  }
  return Array.from(newSelected);
};
const selectAllFiltered = (selectedItems, filteredTree) => {
  var _a;
  if (selectedItems.includes(KEY_ALL) || filteredTree.length === 0) {
    return [];
  }
  const ids = [];
  const queue = [filteredTree[0]];
  while (queue.length) {
    const node = queue.shift();
    if (node) {
      if (node.key !== KEY_ALL) {
        ids.push(node.key);
      }
      if ((_a = node.children) == null ? void 0 : _a.length) {
        queue.push(...node.children);
      }
    }
  }
  ids.push(filteredTree[0].key);
  return ids;
};
const selectAll = (selectedItems, nodeMap, tree) => {
  if (selectedItems.includes(KEY_ALL)) {
    return [];
  }
  const ids = [KEY_ALL, ...getAllItems(nodeMap, tree)];
  return ids;
};
const compressSelectedIds = (tree, selectedIds) => {
  const selectedSet = new Set(selectedIds);
  const result = [];
  const traverse = (node, parentSelected) => {
    var _a;
    const isSelected = selectedSet.has(node.key);
    if (isSelected) {
      result.push(node.key);
      return;
    }
    if ((_a = node.children) == null ? void 0 : _a.length) {
      for (const child of node.children) {
        traverse(child);
      }
    }
  };
  for (const root of tree) {
    traverse(root);
  }
  return result;
};
const expandCompressedIds = (compressedIds, nodeMap) => {
  const expandedIds = /* @__PURE__ */ new Set();
  const collect = (node) => {
    var _a;
    expandedIds.add(node.key);
    (_a = node.children) == null ? void 0 : _a.forEach(collect);
  };
  for (const id of compressedIds) {
    const node = nodeMap.get(id);
    if (node)
      collect(node);
  }
  if (!expandedIds.has(KEY_ALL) && expandedIds.size === nodeMap.size - 1) {
    expandedIds.add(KEY_ALL);
  }
  return [...expandedIds];
};
const filterTreeBySearchQuery = (tree, searchQuery) => {
  if (!searchQuery)
    return { items: tree };
  const lowerQuery = searchQuery.toLowerCase();
  let count = 0;
  const filterNode = (node) => {
    var _a;
    const isMatch = node.label.toLowerCase().includes(lowerQuery);
    const filteredChildren = ((_a = node.children) == null ? void 0 : _a.map(filterNode).filter((node2) => node2 !== null)) || [];
    if (isMatch || filteredChildren.length > 0) {
      count++;
      return {
        ...node,
        children: filteredChildren.length > 0 ? filteredChildren : void 0
      };
    }
    return null;
  };
  return {
    items: tree.map(filterNode).filter((n) => n !== null),
    count
  };
};
const countSelectedDescendants = (node, selectedIds) => {
  let count = 0;
  const traverse = (n) => {
    if (n.children) {
      for (const child of n.children) {
        if (selectedIds.indexOf(child.key) > -1)
          count++;
        traverse(child);
      }
    }
  };
  traverse(node);
  return count;
};
const isArrayEqual = (arr1, arr2) => arr1.length === arr2.length && [...arr1].sort().join() === [...arr2].sort().join();
function highlightMatch(label, searchQuery) {
  if (!searchQuery)
    return label;
  const regex = new RegExp(`(${searchQuery})`, "gi");
  const labelParts = label.split(regex);
  return labelParts.map(
    (labelPart, index) => regex.test(labelPart) ? /* @__PURE__ */ jsx("span", { className: "highlighted", children: labelPart }, index) : labelPart
  );
}
function Label({ id, label, searchQuery, onSelectItem, labelId }) {
  const labelHighlighted = useMemo(() => highlightMatch(label, searchQuery), [label, searchQuery]);
  const handleKeyDown = (event) => {
    if (event.key === " ") {
      onSelectItem(id);
    }
  };
  return /* @__PURE__ */ jsx(
    "span",
    {
      role: "button",
      className: "label",
      onClick: () => onSelectItem(id),
      onKeyDown: handleKeyDown,
      tabIndex: -1,
      id: labelId ?? `label-${id}`,
      children: labelHighlighted
    }
  );
}
const OpenerIcon = "data:image/svg+xml,%3csvg%20width='6'%20height='12'%20viewBox='0%200%206%2012'%20fill='none'%20xmlns='http://www.w3.org/2000/svg'%3e%3cpath%20d='M5.88617%205.72409L0.552834%200.591423C0.337306%200.384005%20-4.76837e-07%200.552348%20-4.76837e-07%200.867332V11.1327C-4.76837e-07%2011.4477%200.337306%2011.616%200.552834%2011.4086L5.88617%206.27591C6.03794%206.12984%206.03794%205.87016%205.88617%205.72409Z'%20fill='%239AA1A8'/%3e%3c/svg%3e";
function OpenButton({ id, isOpened, onToggle }) {
  const className = classNames({
    collapseBtn: true,
    opened: isOpened
  });
  const handleKeyDown = (event) => {
    if (event.key === " ") {
      onToggle(id);
    }
  };
  return /* @__PURE__ */ jsx(
    "div",
    {
      className,
      role: "button",
      onClick: () => onToggle(id),
      onKeyDown: handleKeyDown,
      tabIndex: 0,
      children: /* @__PURE__ */ jsx("img", { src: OpenerIcon, alt: "Toggle list" })
    }
  );
}
function SelectButton({ id, isSelected, onSelect, labelId }) {
  const className = classNames({
    selectBtn: true,
    selected: isSelected
  });
  const ariaLabelProps = labelId ? {
    "aria-labelledby": labelId
  } : {
    "aria-label": `Select ${id}`
  };
  return /* @__PURE__ */ jsx(
    "input",
    {
      type: "checkbox",
      className,
      id: `select-btn-${id}`,
      "data-testid": `select-btn-${id}`,
      checked: isSelected,
      onClick: () => {
        onSelect(id);
      },
      tabIndex: 0,
      ...ariaLabelProps
    }
  );
}
function ListItem({
  idx,
  listItem,
  isNested,
  searchQuery,
  openedItems,
  selectedItems,
  onToggleItem,
  onSelectItem
}) {
  const { label, key: id, children } = listItem;
  const isTopLevel = id === KEY_ALL;
  const isSelected = selectedItems.includes(id);
  const isOpened = openedItems.includes(id) || isTopLevel;
  const isHidden = isTopLevel && searchQuery && isNested;
  const hasChildren = Boolean(children && children.length > 0);
  const countSelectedChildren = useMemo(
    () => countSelectedDescendants(listItem, selectedItems),
    [listItem, selectedItems]
  );
  const className = classNames({
    row: true,
    selected: isSelected
    // hidden: isHidden,
  });
  const isNeedOpenButton = hasChildren && !isTopLevel;
  const isNeedChildrenCount = !isTopLevel && !searchQuery && countSelectedChildren > 0;
  const getNextElementForFocus = (pointer) => {
    const listItems = Array.from(document.querySelectorAll(".selectBtn"));
    const currentIndex = listItems.findIndex((el) => el === document.activeElement);
    const nextIndex = (currentIndex + pointer + listItems.length) % listItems.length;
    return listItems[nextIndex];
  };
  const handleArrowKeys = (e, isOpened2, hasChildren2, id2, idx2, onToggleItem2) => {
    if (e.key === "ArrowRight" && !isOpened2) {
      onToggleItem2(id2);
    } else if (e.key === "ArrowLeft" && isOpened2) {
      onToggleItem2(id2);
    }
    if (e.key === "ArrowRight" && isOpened2 && hasChildren2 && id2 !== KEY_ALL || e.key === "ArrowDown") {
      const el = getNextElementForFocus(1);
      el.focus();
    } else if (e.key === "ArrowLeft" && !isOpened2 && idx2 === 0 || e.key === "ArrowUp") {
      const el = getNextElementForFocus(-1);
      if (e.key !== "ArrowLeft" || el.id !== "select-btn-all") {
        el.focus();
      }
    }
  };
  const handleKeyDown = (e) => {
    if (["ArrowRight", "ArrowLeft", "ArrowUp", "ArrowDown"].includes(e.key)) {
      e.stopPropagation();
      e.preventDefault();
      handleArrowKeys(e, isOpened, hasChildren, id, idx, onToggleItem);
    }
  };
  const labelId = `multidropdown-list-item-label-${id}`;
  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    /* @__PURE__ */ jsxs("li", { onKeyDown: handleKeyDown, className: "multidropdown-list-item", children: [
      !isHidden && /* @__PURE__ */ jsxs("div", { className, children: [
        /* @__PURE__ */ jsx(
          SelectButton,
          {
            id,
            isSelected,
            onSelect: onSelectItem,
            labelId
          }
        ),
        isNeedOpenButton && /* @__PURE__ */ jsx(OpenButton, { id, onToggle: onToggleItem, isOpened }),
        /* @__PURE__ */ jsx(
          Label,
          {
            id,
            label: label + (isNeedChildrenCount ? ` (${countSelectedChildren + (isSelected ? 1 : 0)})` : ""),
            searchQuery,
            onSelectItem,
            labelId
          }
        )
      ] }),
      hasChildren && isOpened && /* @__PURE__ */ jsx(
        List,
        {
          list: children,
          isNested,
          searchQuery,
          openedItems,
          selectedItems,
          onToggleItem,
          onSelectItem,
          isOpened,
          noOffset: isTopLevel
        }
      )
    ] })
  );
}
function List({
  list,
  searchQuery,
  isOpened,
  noOffset,
  openedItems,
  selectedItems,
  isNested,
  onToggleItem,
  onSelectItem
}) {
  const className = classNames({
    list: true,
    opened: isOpened,
    main: noOffset
  });
  return /* @__PURE__ */ jsx("ul", { className, children: list.map((listItem, idx) => /* @__PURE__ */ jsx(
    ListItem,
    {
      idx,
      listItem,
      isNested,
      searchQuery,
      openedItems,
      selectedItems,
      onToggleItem,
      onSelectItem
    },
    listItem.key
  )) });
}
function Popup({
  list,
  searchQuery,
  openedItems,
  selectedItems,
  isNested,
  onReset,
  onSave,
  onToggleItem,
  onSelectItem,
  onClickOverlay
}) {
  useEffect(() => {
    document.addEventListener("mousedown", onClickOverlay);
    return () => {
      document.removeEventListener("mousedown", onClickOverlay);
    };
  }, []);
  const emptyListMessage = list.length === 0 ? searchQuery ? "No results found for search term" : "No available options" : "";
  return /* @__PURE__ */ jsxs("div", { className: "popup", role: "listbox", children: [
    emptyListMessage ? /* @__PURE__ */ jsx("span", { className: "emptyListMessage", children: emptyListMessage }) : /* @__PURE__ */ jsx("div", { className: "tree", id: "dropdown-list", children: /* @__PURE__ */ jsx(
      List,
      {
        list,
        selectedItems,
        openedItems,
        isNested,
        onSelectItem,
        onToggleItem,
        searchQuery,
        noOffset: true,
        isOpened: true
      }
    ) }),
    /* @__PURE__ */ jsxs("div", { className: "toolbar", children: [
      /* @__PURE__ */ jsx(Button, { styling: "white", onClick: onReset, children: "Reset" }),
      /* @__PURE__ */ jsx(Button, { styling: "primary", onClick: onSave, children: "Save" })
    ] })
  ] });
}
function MultiDropdown({
  options,
  title = "",
  selected = [],
  placeholder = "Select items",
  isNested = false,
  disabled = false,
  autoSave = false,
  onChange
}) {
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [openedItems, setOpenedItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState(selected);
  const [status, setStatus] = useState("");
  const data = useMemo(() => {
    return [
      {
        key: KEY_ALL,
        label: "Select all",
        children: options
      }
    ];
  }, [options]);
  const [parentMap, nodeMap] = useMemo(() => {
    const parentMap2 = /* @__PURE__ */ new Map();
    const nodeMap2 = /* @__PURE__ */ new Map();
    return buildMaps(parentMap2, nodeMap2, data);
  }, [data]);
  const label = useMemo(() => {
    const selectedItemsLength = selectedItems.length;
    let label2 = placeholder;
    if (selectedItems.includes(KEY_ALL)) {
      label2 = "All selected";
    } else if (selectedItemsLength > 0) {
      label2 = `${selectedItemsLength} selected`;
    }
    return label2;
  }, [selectedItems]);
  useEffect(() => {
    if (!isEdited) {
      setSelectedItems(expandCompressedIds(selected, nodeMap));
    }
  }, [selected, nodeMap]);
  useEffect(() => {
    var _a;
    if (isPopupOpen) {
      (_a = searchInputRef.current) == null ? void 0 : _a.focus();
    } else if (autoSave && !isArrayEqual(selectedItems, selected)) {
      onChange(selectedItems, compressSelectedIds(data, selectedItems));
    }
  }, [isPopupOpen]);
  const onOpenPopup = () => {
    if (disabled)
      return;
    setIsPopupOpen(!isPopupOpen);
  };
  const notifySearchUpdated = useMemo(
    () => debounce((count) => {
      if (count === void 0) {
        setStatus("");
      } else {
        setStatus(`Select items updated, list of ${count} items`);
      }
    }, 200),
    []
  );
  const filteredTree = useMemo(() => {
    const { items, count } = filterTreeBySearchQuery(data, searchQuery);
    notifySearchUpdated(count);
    return items;
  }, [data, searchQuery]);
  const onSelectItem = (id) => {
    if (!isEdited) {
      setIsEdited(true);
    }
    let newSelectedItems = [];
    if (id === KEY_ALL && searchQuery && !isNested) {
      newSelectedItems = selectAllFiltered(selectedItems, filteredTree);
    } else if (id === KEY_ALL) {
      newSelectedItems = selectAll(selectedItems, nodeMap, data);
    } else {
      newSelectedItems = toggleSelectedId(selectedItems, parentMap, nodeMap, id);
    }
    setSelectedItems(newSelectedItems);
  };
  const onToggleItem = (id) => {
    setOpenedItems(
      openedItems.includes(id) ? openedItems.filter((val) => val !== id) : [...openedItems, id]
    );
  };
  const onReset = () => {
    setSelectedItems([]);
    onSearchReset();
  };
  const onSearchClick = (event) => {
    event.stopPropagation();
  };
  const onSearchQueryChange = (event) => {
    setSearchQuery(event.target.value);
  };
  const onSave = () => {
    let filteredSelectedItems = selectedItems;
    if (selectedItems[selectedItems.length - 1] === KEY_ALL) {
      filteredSelectedItems = selectedItems.slice(0, selectedItems.length - 1);
      setSelectedItems(filteredSelectedItems);
    }
    onChange(filteredSelectedItems, compressSelectedIds(data, filteredSelectedItems));
    setIsPopupOpen(false);
    onSearchReset();
  };
  const onClickOverlay = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsPopupOpen(false);
    }
  };
  const onSearchReset = () => {
    setSearchQuery("");
    setStatus("");
  };
  const moveFocusToFirstNode = () => {
    const topNode = document.getElementById(`select-btn-${KEY_ALL}`);
    if (topNode) {
      topNode.focus();
    }
  };
  const moveFocusToLastNode = () => {
    var _a;
    const topOptions = searchQuery ? data : data[0].children;
    const lastTopNode = document.getElementById(
      `select-btn-${(_a = topOptions == null ? void 0 : topOptions.slice(-1)[0]) == null ? void 0 : _a.key}`
    );
    if (lastTopNode) {
      lastTopNode.focus();
    }
  };
  const handleKeyDown = (event) => {
    if (event.key === "Home") {
      moveFocusToFirstNode();
    } else if (event.key === "End") {
      moveFocusToLastNode();
    } else if (event.key === "Escape") {
      setIsPopupOpen(false);
    } else if (event.key === "Enter") {
      onOpenPopup();
    }
  };
  useEffect(() => {
    if (!searchQuery) {
      setOpenedItems([]);
      return;
    }
    const items = getAllItems(nodeMap, data);
    setOpenedItems(items);
  }, [searchQuery]);
  const className = classNames({
    "sa-multidropdown": true,
    opened: isPopupOpen,
    disabled
  });
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className,
      ref: dropdownRef,
      role: "combobox",
      title: title || "Dropdown",
      "aria-label": title || "Dropdown",
      "aria-haspopup": "listbox",
      "aria-expanded": isPopupOpen,
      "aria-controls": "dropdown-list",
      onKeyDown: handleKeyDown,
      tabIndex: 0,
      "aria-labelledby": `${title}-dropdown-label`,
      children: [
        /* @__PURE__ */ jsx(
          "span",
          {
            "data-testid": "multidropdown-status",
            "aria-atomic": "true",
            className: "sa-multidropdown-status--hidden",
            "aria-live": "polite",
            children: status
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "inputBox", role: "button", onClick: onOpenPopup, "data-testid": "dropdown-input-box", children: [
          isPopupOpen ? /* @__PURE__ */ jsx(
            "input",
            {
              className: "searchInput",
              ref: searchInputRef,
              type: "text",
              value: searchQuery,
              onClick: onSearchClick,
              onChange: onSearchQueryChange,
              "aria-label": "Search input box"
            }
          ) : /* @__PURE__ */ jsx(
            "span",
            {
              className: "label",
              id: `${title}-dropdown-label`,
              "data-testid": "dropdown-value",
              "aria-label": label ?? "Placeholder",
              children: label
            }
          ),
          /* @__PURE__ */ jsx("div", { className: "openBtn" })
        ] }),
        isPopupOpen && /* @__PURE__ */ jsx(
          Popup,
          {
            list: filteredTree,
            searchQuery,
            selectedItems,
            openedItems,
            isNested,
            onSelectItem,
            onToggleItem,
            onReset,
            onSave,
            onClickOverlay
          }
        )
      ]
    }
  );
}
export {
  MultiDropdown as default
};
